import { lazy, Suspense, useEffect, useState, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  AvatarGroup,
} from '@mui/material'
import {
  Add,
  FolderOpen,
  Assignment,
  TrendingUp,
  Edit,
  ErrorOutline,
} from '@mui/icons-material'

import { AppDispatch, RootState } from '../store'
import { fetchProjects, fetchProject } from '../store/slices/projectSlice'
import { fetchMyBoardData } from '../store/slices/issueSlice'
import { Project, Issue } from '../types'
import LoadingSpinner from '../components/common/LoadingSpinner'
import CreateProjectDialog from '../components/projects/CreateProjectDialog'
import EditProjectDialog from '../components/projects/EditProjectDialog'
import MyDayList from '../components/dashboard/MyDayList'
import OnLeaveToday from '../components/dashboard/OnLeaveToday'
import { bucketIssuesByUrgency, isIssueDone } from '../utils/issueUrgency'
import { getNonLeadMembers, getTotalMemberCount } from '../utils/projectMembers'
import { hasPermission, PERMISSIONS } from '../utils/roleManagement'
import { getInitials } from '../utils/userColors'
import PageHeader from '../shared-components/PageHeader'
import EmptyState from '../shared-components/EmptyState'
import StatCardRow from '../shared-components/StatCardRow'

const IssueDetailModal = lazy(() => import('../components/issues/IssueDetailModal'))

const DashboardPage: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { user } = useSelector((state: RootState) => state.auth)
  const { projects, loading: projectsLoading } = useSelector((state: RootState) => state.projects)
  const { issues: myIssues, loading: issuesLoading } = useSelector((state: RootState) => state.issues)
  const [createProjectOpen, setCreateProjectOpen] = useState(false)
  const [editProjectOpen, setEditProjectOpen] = useState(false)
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null)
  const [issueDetailOpen, setIssueDetailOpen] = useState(false)
  const projectsFetchRef = useRef<boolean>(false)
  const myDayFetchRef = useRef<boolean>(false)

  useEffect(() => {
    // Prevent duplicate calls in StrictMode
    if (projectsFetchRef.current) return
    projectsFetchRef.current = true
    dispatch(fetchProjects({ limit: 6 }))
  }, [dispatch])

  useEffect(() => {
    // Prevent duplicate calls in StrictMode
    if (myDayFetchRef.current) return
    myDayFetchRef.current = true
    // includeCompleted: true - "done" filtering for My Day (which also treats
    // live/qa_live as done) happens client-side via isIssueDone, not via this
    // backend flag, which only excludes completed/legacy done and is shared
    // with MyBoardPage where live issues should still be visible.
    dispatch(fetchMyBoardData({ includeCompleted: true }))
  }, [dispatch])

  const activeIssues = useMemo(() => myIssues.filter((issue) => !isIssueDone(issue)), [myIssues])
  const buckets = useMemo(() => bucketIssuesByUrgency(myIssues, { dueSoonDays: 5 }), [myIssues])

  if (projectsLoading || issuesLoading) {
    return <LoadingSpinner message="Loading dashboard..." />
  }

  const handleIssueClick = (issue: Issue) => {
    setSelectedIssue(issue)
    setIssueDetailOpen(true)

    const project = issue.project
    const projectId =
      project && typeof project === 'object'
        ? (project as any)._id
        : typeof project === 'string'
          ? project
          : null

    if (projectId) {
      dispatch(fetchProject(projectId))
    }
  }

  const handleIssueDetailClose = () => {
    setIssueDetailOpen(false)
    setSelectedIssue(null)
  }

  const handleIssueUpdate = (updatedIssue: Issue) => {
    setSelectedIssue((prev) => (prev?._id === updatedIssue._id ? updatedIssue : prev))
  }

  const handleEditProject = (e: React.MouseEvent, project: Project) => {
    e.stopPropagation() // Prevent card click
    setSelectedProject(project)
    setEditProjectOpen(true)
  }

  return (
    <Box>
      <PageHeader
        title={<>Welcome back, {user?.name}!</>}
        subtitle="Here's what's happening with your projects today."
        sx={{ mb: 4 }}
      />

      {/* Quick Stats */}
      <StatCardRow
        layout="grid"
        sx={{ mb: 4 }}
        items={[
          { label: 'Active Projects', value: projects.length, color: 'primary.main', icon: <FolderOpen /> },
          { label: 'Issues Assigned', value: activeIssues.length, color: 'success.main', icon: <Assignment /> },
          {
            label: 'In Progress',
            value: activeIssues.filter((issue) => issue.status.category === 'in_progress').length,
            color: 'warning.main',
            icon: <TrendingUp />,
          },
          { label: 'Overdue', value: buckets.overdue.length, color: 'error.main', icon: <ErrorOutline /> },
        ]}
      />

      {/* My Day */}
      <MyDayList buckets={buckets} onIssueClick={handleIssueClick} />

      {/* On Leave Today */}
      <OnLeaveToday />

      {/* Recent Projects */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Recent Projects</Typography>
          {user && hasPermission(user.role, PERMISSIONS.CREATE_PROJECTS) && (
            <Button 
              variant="contained" 
              startIcon={<Add />}
              onClick={() => setCreateProjectOpen(true)}
            >
              Create Project
            </Button>
          )}
        </Box>

        <Grid container spacing={3}>
          {projects.map((project) => (
            <Grid item xs={12} md={6} lg={4} key={project._id}>
              <Card sx={{ height: '100%' }}>
                <Box
                  onClick={() => navigate(`/projects/${project._id}/kanban`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(`/projects/${project._id}/kanban`)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    borderRadius: 'inherit',
                    transition: 'background-color 0.15s ease',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.03)' },
                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
                  }}
                >
                  <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {project.name}
                      </Typography>
                      <Chip label={project.key} size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={project.status} 
                        size="small" 
                        color={project.status === 'active' ? 'success' : 'default'}
                      />
                      {user && hasPermission(user.role, PERMISSIONS.EDIT_PROJECTS) && (
                        <Button
                          size="small"
                          startIcon={<Edit />}
                          onClick={(e) => handleEditProject(e, project)}
                          sx={{ minWidth: 'auto', p: 0.5 }}
                        >
                          Edit
                        </Button>
                      )}
                    </Box>
                  </Box>

                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {project.description || 'No description available'}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24 } }}>
                      <Avatar src={project.lead.avatar}>
                        {getInitials(project.lead.name, '', false)}
                      </Avatar>
                      {getNonLeadMembers(project).slice(0, 2).map((member, index) => (
                        <Avatar key={index} src={typeof member.user !== 'string' ? member.user.avatar : ''}>
                          {typeof member.user !== 'string' ? getInitials(member.user.name, '', false) : ''}
                        </Avatar>
                      ))}
                    </AvatarGroup>
                    <Typography variant="caption" color="textSecondary">
                      {getTotalMemberCount(project)} members
                    </Typography>
                  </Box>
                  </CardContent>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {projects.length === 0 && (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <EmptyState
                icon={<FolderOpen sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />}
                title="No projects yet"
                subtitleColor="textSecondary"
                subtitle="Create your first project to get started with issue tracking and team collaboration."
                subtitleSx={{ mb: 3 }}
                action={
                  user && hasPermission(user.role, PERMISSIONS.CREATE_PROJECTS) && (
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => setCreateProjectOpen(true)}
                    >
                      Create Your First Project
                    </Button>
                  )
                }
              />
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Create Project Dialog */}
      <CreateProjectDialog
        open={createProjectOpen}
        onClose={() => setCreateProjectOpen(false)}
      />

      {/* Edit Project Dialog */}
      <EditProjectDialog
        open={editProjectOpen}
        onClose={() => setEditProjectOpen(false)}
        project={selectedProject}
      />

      {/* Issue Detail Modal (opened from My Day) */}
      <Suspense fallback={null}>
        <IssueDetailModal
          open={issueDetailOpen}
          onClose={handleIssueDetailClose}
          issue={selectedIssue}
          onIssueUpdate={handleIssueUpdate}
        />
      </Suspense>
    </Box>
  )
}

export default DashboardPage
